import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { reportApi, leaveTypeApi, userApi } from '@/services/api';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Download, Filter, FileText, BarChart2, CalendarDays, ChevronDown, ChevronRight, Search } from 'lucide-react';
import { format, startOfMonth, endOfMonth, parseISO } from 'date-fns';
import { useAuthStore } from '@/store/authStore';
import { useSearchParams } from 'react-router-dom';

function SearchableSelect({ options, value, onChange, placeholder }: any) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filtered = options.filter((o: any) => o.label.toLowerCase().includes(search.toLowerCase()));
  const selectedOption = options.find((o: any) => o.value === value);

  return (
    <div className="relative w-full" ref={ref}>
      <div 
        className="w-full rounded-md border border-slate-300 text-sm px-3 py-2 bg-white cursor-pointer flex justify-between items-center min-h-[38px] hover:border-slate-400 transition-colors"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="truncate">{selectedOption ? selectedOption.label : placeholder}</span>
        <ChevronDown className="w-4 h-4 text-slate-400" />
      </div>
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-xl ring-1 ring-black ring-opacity-5">
          <div className="p-2 border-b border-slate-100 bg-slate-50/50 rounded-t-md relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              autoFocus
              className="w-full pl-8 pr-3 py-1.5 text-sm border border-slate-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Search..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <div className="overflow-y-auto py-1 custom-scrollbar" style={{ maxHeight: '250px' }}>
            <div 
              className="px-3 py-2 text-sm hover:bg-slate-100 cursor-pointer text-slate-600"
              onClick={() => { onChange(''); setIsOpen(false); setSearch(''); }}
            >
              {placeholder}
            </div>
            {filtered.map((o: any) => (
              <div
                key={o.value}
                className={`px-3 py-2 text-sm hover:bg-slate-100 cursor-pointer truncate ${value === o.value ? 'bg-blue-50 text-blue-700 font-medium' : 'text-slate-700'}`}
                onClick={() => { onChange(o.value); setIsOpen(false); setSearch(''); }}
              >
                {o.label}
              </div>
            ))}
            {filtered.length === 0 && (
              <div className="px-3 py-3 text-sm text-slate-500 text-center">No results found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function ReportsPage() {
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') === 'BALANCE' ? 'BALANCE' : 'REGISTER';
  const initialUserId = searchParams.get('userId') || '';
  const [activeTab, setActiveTab] = useState<'REGISTER' | 'BALANCE'>(initialTab);
  const [dateRange, setDateRange] = useState({
    from: format(startOfMonth(new Date()), 'yyyy-MM-dd'),
    to: format(endOfMonth(new Date()), 'yyyy-MM-dd')
  });
  const [department, setDepartment] = useState('');
  const [userId, setUserId] = useState(initialUserId);
  const [leaveTypeId, setLeaveTypeId] = useState('');
  const [status, setStatus] = useState('');
  const [expandedUsers, setExpandedUsers] = useState<Set<string>>(initialUserId ? new Set([initialUserId]) : new Set());
  const { user } = useAuthStore();
  const isManager = user?.role === 'MANAGER' || user?.role === 'SENIOR_MANAGER';

  const toggleUser = (uid: string) => {
    setExpandedUsers(prev => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return next;
    });
  };

  const { data: leaveTypes = [] } = useQuery({
    queryKey: ['leave-types'],
    queryFn: async () => {
      const res = await leaveTypeApi.getAll();
      return res.data.data;
    },
  });

  const { data: allUsers = [] } = useQuery({
    queryKey: ['users', user?.id, isManager],
    queryFn: async () => {
      const res = isManager
        ? await userApi.getDirectReports(user!.id)
        : await userApi.getAll();
      return Array.isArray(res.data?.data) ? res.data.data : [];
    },
  });

  const departments = useMemo(() => {
    const depts = new Set((allUsers as any[]).map((u: any) => u.department).filter(Boolean));
    return Array.from(depts).sort();
  }, [allUsers]);

  const { data: registerData, isLoading: isLoadingRegister } = useQuery({
    queryKey: ['reports', 'register', dateRange, department, userId, leaveTypeId, status],
    queryFn: async () => {
      const res = await reportApi.leaveRegister({
        startDate: dateRange.from,
        endDate: dateRange.to,
        department: department || undefined,
        userId: userId || undefined,
        leaveTypeId: leaveTypeId || undefined,
        status: status || undefined,
      });
      // backend returns { success, data: [...], total }
      return { data: res.data.data ?? [], total: res.data.total ?? 0 };
    },
    enabled: activeTab === 'REGISTER'
  });

  const { data: balanceData, isLoading: isLoadingBalance } = useQuery({
    queryKey: ['reports', 'balance', department, userId, leaveTypeId],
    queryFn: async () => {
      const res = await reportApi.balanceSummary({
        department: department || undefined,
        userId: userId || undefined,
        leaveTypeId: leaveTypeId || undefined,
      });
      return res.data.data;
    },
    enabled: activeTab === 'BALANCE'
  });

  const groupedBalances = useMemo(() => {
    if (!balanceData) return [];
    const groups: Record<string, any> = {};
    balanceData.forEach((row: any) => {
      if (!groups[row.userId]) {
        groups[row.userId] = {
          user: row.user,
          userId: row.userId,
          balances: [],
          totalBase: 0,
          totalCarried: 0,
          totalUsed: 0,
          totalPending: 0,
          totalAvailable: 0,
        };
      }
      groups[row.userId].balances.push(row);
      groups[row.userId].totalBase += row.total;
      groups[row.userId].totalCarried += row.carriedOver;
      groups[row.userId].totalUsed += row.used;
      groups[row.userId].totalPending += row.pending;
      groups[row.userId].totalAvailable += row.available;
    });
    return Object.values(groups).sort((a: any, b: any) => a.user.name.localeCompare(b.user.name));
  }, [balanceData]);

  const handleExportCSV = () => {
    let csvContent = "data:text/csv;charset=utf-8,";
    
    if (activeTab === 'REGISTER' && registerData) {
      csvContent += "Employee ID,Name,Department,Leave Type,Start Date,End Date,Duration,Status\\n";
      registerData.data.forEach((row: any) => {
        csvContent += `${row.user.employeeId},${row.user.name},${row.user.department},${row.leaveType.name},${format(parseISO(row.fromDate),'yyyy-MM-dd')},${format(parseISO(row.toDate),'yyyy-MM-dd')},${row.duration},${row.status}\\n`;
      });
    } else if (activeTab === 'BALANCE' && balanceData) {
      csvContent += "Employee ID,Name,Department,Leave Type,Total Quota,Carried Over,Used,Pending,Available\\n";
      balanceData.forEach((row: any) => {
        csvContent += `${row.user.employeeId},${row.user.name},${row.user.department},${row.leaveType.name},${row.total},${row.carriedOver},${row.used},${row.pending},${row.available}\\n`;
      });
    }

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `${activeTab.toLowerCase()}_report_${format(new Date(), 'yyyyMMdd')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <Button onClick={handleExportCSV} variant="secondary" className="shadow-sm">
          <Download className="w-4 h-4 mr-2" /> Export to CSV
        </Button>
      </div>

      <div className="bg-white p-1 rounded-xl border border-slate-200 inline-flex">
        <button
          onClick={() => setActiveTab('REGISTER')}
          className={`px-6 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
            activeTab === 'REGISTER' ? 'bg-slate-100 text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          <CalendarDays className="w-4 h-4" /> Leave Register
        </button>
        <button
          onClick={() => setActiveTab('BALANCE')}
          className={`px-6 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
            activeTab === 'BALANCE' ? 'bg-slate-100 text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          <BarChart2 className="w-4 h-4" /> Balance Summary
        </button>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        {/* Filters */}
        <div className="p-4 border-b border-slate-200 bg-slate-50 grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
          {activeTab === 'REGISTER' && (
            <>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">From Date</label>
                <input 
                  type="date" 
                  value={dateRange.from}
                  onChange={e => setDateRange(prev => ({...prev, from: e.target.value}))}
                  className="w-full rounded-md border-slate-300 text-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">To Date</label>
                <input 
                  type="date" 
                  value={dateRange.to}
                  onChange={e => setDateRange(prev => ({...prev, to: e.target.value}))}
                  className="w-full rounded-md border-slate-300 text-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
            </>
          )}
          
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Department</label>
            <select
              value={department}
              onChange={e => setDepartment(e.target.value)}
              className="w-full rounded-md border-slate-300 text-sm focus:border-blue-500 focus:ring-blue-500"
            >
              <option value="">All Departments</option>
              {departments.map((d: any) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Employee</label>
            <SearchableSelect
              options={allUsers.map((u: any) => ({ value: u.id, label: `${u.name} (${u.employeeId})` }))}
              value={userId}
              onChange={(val: string) => setUserId(val)}
              placeholder="All Employees"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Leave Type</label>
            <select 
              value={leaveTypeId}
              onChange={e => setLeaveTypeId(e.target.value)}
              className="w-full rounded-md border-slate-300 text-sm focus:border-blue-500 focus:ring-blue-500"
            >
              <option value="">All Types</option>
              {leaveTypes.map((t:any) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>

          {activeTab === 'REGISTER' && (
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Status</label>
              <select 
                value={status}
                onChange={e => setStatus(e.target.value)}
                className="w-full rounded-md border-slate-300 text-sm focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="">All Statuses</option>
                <option value="APPROVED">Approved</option>
                <option value="PENDING">Pending</option>
                <option value="REJECTED">Rejected</option>
                <option value="CANCELLED">Cancelled</option>
              </select>
            </div>
          )}
        </div>

        {/* Data Table */}
        <div className="overflow-x-auto">
          {activeTab === 'REGISTER' ? (
            <table className="w-full text-left text-sm text-slate-600">
              <thead className="bg-white text-xs uppercase font-semibold text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-4">Employee</th>
                  <th className="px-6 py-4">Department</th>
                  <th className="px-6 py-4">Leave Type</th>
                  <th className="px-6 py-4">Dates</th>
                  <th className="px-6 py-4">Days</th>
                  <th className="px-6 py-4">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {isLoadingRegister ? (
                  <tr><td colSpan={6} className="p-4"><div className="h-32 bg-slate-100 animate-pulse rounded"></div></td></tr>
                ) : registerData?.data?.length === 0 ? (
                  <tr><td colSpan={6} className="p-8 text-center text-slate-500">No records found for this period.</td></tr>
                ) : registerData?.data?.map((row: any) => (
                  <tr key={row.id} className="hover:bg-slate-50">
                    <td className="px-6 py-3 font-medium text-slate-800">
                      {row.user.name} <span className="text-xs text-slate-400 font-normal ml-1">({row.user.employeeId})</span>
                    </td>
                    <td className="px-6 py-3">{row.user.department}</td>
                    <td className="px-6 py-3">
                      <span className="flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full" style={{backgroundColor: row.leaveType.colour}}></span>
                        {row.leaveType.name}
                      </span>
                    </td>
                    <td className="px-6 py-3 text-xs">
                      {format(parseISO(row.fromDate), 'dd MMM yyyy')} - {format(parseISO(row.toDate), 'dd MMM yyyy')}
                    </td>
                    <td className="px-6 py-3 font-semibold text-slate-700">{row.duration}</td>
                    <td className="px-6 py-3"><Badge status={row.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <table className="w-full text-left text-sm text-slate-600">
              <thead className="bg-slate-50 text-xs uppercase font-semibold text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-4 w-10"></th>
                  <th className="px-6 py-4">Employee / Leave Type</th>
                  <th className="px-6 py-4 text-center">Base Quota</th>
                  <th className="px-6 py-4 text-center">Carried Over</th>
                  <th className="px-6 py-4 text-center">Used</th>
                  <th className="px-6 py-4 text-center">Pending</th>
                  <th className="px-6 py-4 text-center font-bold text-slate-800">Available</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {isLoadingBalance ? (
                  <tr><td colSpan={7} className="p-4"><div className="h-32 bg-slate-100 animate-pulse rounded"></div></td></tr>
                ) : groupedBalances?.length === 0 ? (
                  <tr><td colSpan={7} className="p-8 text-center text-slate-500">No records found.</td></tr>
                ) : groupedBalances?.map((group: any) => {
                  const isExpanded = expandedUsers.has(group.userId);
                  return (
                    <React.Fragment key={group.userId}>
                      {/* Parent Row (User Totals) */}
                      <tr 
                        className="hover:bg-slate-50 cursor-pointer transition-colors bg-white group"
                        onClick={() => toggleUser(group.userId)}
                      >
                        <td className="px-6 py-4">
                          <button className="p-1 rounded-md hover:bg-slate-200 text-slate-400 group-hover:text-slate-600 transition-colors">
                            {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          </button>
                        </td>
                        <td className="px-6 py-4 font-semibold text-slate-800 text-base">
                          {group.user.name} <span className="text-xs text-slate-400 font-normal ml-1">({group.user.employeeId})</span>
                        </td>
                        <td className="px-6 py-4 text-center font-medium text-slate-600">{group.totalBase}</td>
                        <td className="px-6 py-4 text-center font-medium text-slate-600">{group.totalCarried}</td>
                        <td className="px-6 py-4 text-center font-medium text-slate-600">{group.totalUsed}</td>
                        <td className="px-6 py-4 text-center font-medium text-amber-600">{group.totalPending > 0 ? group.totalPending : '-'}</td>
                        <td className="px-6 py-4 text-center font-bold text-slate-800 text-lg bg-blue-50/30">{group.totalAvailable}</td>
                      </tr>
                      
                      {/* Child Rows (Leave Types) */}
                      {isExpanded && group.balances.map((row: any) => (
                        <tr key={row.id} className="bg-slate-50/80 hover:bg-slate-100/80 text-sm">
                          <td className="px-6 py-3 border-l-4 border-l-blue-400"></td>
                          <td className="px-6 py-3 pl-8">
                            <span className="flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full" style={{backgroundColor: row.leaveType.colour}}></span>
                              <span className="font-medium text-slate-700">{row.leaveType.name}</span>
                              <span className="text-[10px] bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded uppercase">{row.leaveType.code}</span>
                            </span>
                          </td>
                          <td className="px-6 py-3 text-center text-slate-500">{row.total}</td>
                          <td className="px-6 py-3 text-center text-slate-500">{row.carriedOver}</td>
                          <td className="px-6 py-3 text-center text-slate-500">{row.used}</td>
                          <td className="px-6 py-3 text-center text-amber-600/80">{row.pending > 0 ? row.pending : '-'}</td>
                          <td className="px-6 py-3 text-center font-semibold text-slate-700">{row.isUnlimited ? 'Unlimited' : row.available}</td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
